// src/services/api.js
import axios from 'axios';
import apiConfig from '../config/api.config';
import authConfig from '../config/auth.config';

// Create axios instance with base URL for main backend API
const api = axios.create({
  baseURL: apiConfig.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Add withCredentials for CORS with cookies if needed
  withCredentials: false
});

// Add a request interceptor to include session ID in all requests
api.interceptors.request.use(
  (config) => {
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    
    // Log the request method and URL for debugging
    //console.log(`${config.method?.toUpperCase() || 'REQUEST'} ${config.url}`, config);
    
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }
    
    // CONSISTENT HEADER HANDLING FOR ALL REQUEST TYPES
    // Get user info from localStorage
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.username) {
          // Set headers for all request types
          config.headers['X-User-Name'] = user.username;
          config.headers['x-user-name'] = user.username;
          //console.log(`Setting username header for ${config.method} request:`, user.username);
        }
        if (user.role) {
          // Set headers for all request types
          config.headers['X-User-Role'] = user.role;
          config.headers['x-user-role'] = user.role;
          //console.log(`Setting role header for ${config.method} request:`, user.role);
        }
      } catch (e) {
        console.warn(`Failed to parse user data for request`, e);
        // Set default values
        config.headers['x-user-name'] = 'unknown';
        config.headers['X-User-Name'] = 'unknown';
        config.headers['x-user-role'] = 'user';
        config.headers['X-User-Role'] = 'user';
      }
    } else {
      // No user in localStorage, set default values
      console.warn('No user found in localStorage for request');
      config.headers['x-user-name'] = 'unknown';
      config.headers['X-User-Name'] = 'unknown';
      config.headers['x-user-role'] = 'user';
      config.headers['X-User-Role'] = 'user';
    }
    
    // Keep only safe headers to avoid CORS issues, but preserve user info headers
    const safeHeaders = { 
      'Content-Type': 'application/json',
      'Authorization': config.headers.Authorization,
      'X-User-Name': config.headers['X-User-Name'] || config.headers['x-user-name'] || 'unknown',
      'X-User-Role': config.headers['X-User-Role'] || config.headers['x-user-role'] || 'user'
    };
    
    // Replace all headers with the safe ones plus user info headers
    config.headers = safeHeaders;
    
    // Add debug information for server deployment issues
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, { 
      params: config.params,
      headers: { ...config.headers, Authorization: config.headers.Authorization ? '[REDACTED]' : undefined }
    });
    
    // Log final headers for debugging
    //console.log(`Final headers for ${config.method?.toUpperCase() || 'REQUEST'} ${config.url}:`, config.headers);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle CORS errors
    if (error.message && error.message.includes('Network Error')) {
      console.warn('Network error - possibly CORS related:', error);
      // You could dispatch an action to show a user-friendly message
    }
    
    // Log request details for failed requests
    if (error.config) {
      console.error('Request failed:', {
        method: error.config.method?.toUpperCase(),
        url: error.config.url,
        headers: error.config.headers,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
    }
    
    return Promise.reject(error);
  }
);

// Create a separate axios instance for auth API
const authApi = axios.create({
  baseURL: authConfig.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add an API method for user details with proper authorization
api.getUserDetails = async (sessionId) => {
  try {
    // Get token from localStorage or use the default token
    let token = localStorage.getItem('token');
    token = token ? JSON.parse(token) : authConfig.DEFAULT_AUTH_TOKEN;
    
    // Use the auth API instance with authorization header
    const response = await authApi.get(authConfig.AUTH_ENDPOINT, {
      params: { session_id: sessionId },
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    });
    
    //console.log("getUserDetails response:", response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching user details:', error);
    return { status: false, error: error.message };
  }
};

// Add helper methods to ensure headers are set for specific request types
api.postWithAuth = async (url, data, options = {}) => {
  // Get user info from localStorage
  const userStr = localStorage.getItem('user');
  const headers = options.headers || {};
  
  // Log the current state of headers before modification
  //console.log('Initial headers for POST request:', headers);
  
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      //console.log('User from localStorage:', user);
      
      if (user.username) {
        // Set both uppercase and lowercase versions to ensure compatibility
        headers['x-user-name'] = user.username;
        headers['X-User-Name'] = user.username;
        //console.log(`Setting username headers for POST request: ${user.username}`);
      } else {
        headers['x-user-name'] = 'unknown';
        headers['X-User-Name'] = 'unknown';
      }
      
      if (user.role) {
        // Set both uppercase and lowercase versions to ensure compatibility
        headers['x-user-role'] = user.role;
        headers['X-User-Role'] = user.role;
        //console.log(`Setting role headers for POST request: ${user.role}`);
      } else {
        headers['x-user-role'] = 'user';
        headers['X-User-Role'] = 'user';
      }
    } catch (e) {
      console.warn('Failed to parse user data in postWithAuth', e);
      // Set default values
      headers['x-user-name'] = 'unknown';
      headers['X-User-Name'] = 'unknown';
      headers['x-user-role'] = 'user';
      headers['X-User-Role'] = 'user';
    }
  } else {
    console.warn('No user found in localStorage for POST request');
    // No user in localStorage, set default values
    headers['x-user-name'] = 'unknown';
    headers['X-User-Name'] = 'unknown';
    headers['x-user-role'] = 'user';
    headers['X-User-Role'] = 'user';
  }
  
  // Log the final headers before making the request
  //console.log('Final POST request headers:', headers);
  //console.log('POST request URL:', url);
  //console.log('POST request data:', data);
  
  // Make the POST request with the updated headers
  // Ensure we're using the axios instance that has the interceptor
  return api.post(url, data, { 
    ...options, 
    headers: {
      ...headers,
      'X-User-Name': headers['X-User-Name'] || headers['x-user-name'] || 'unknown',
      'X-User-Role': headers['X-User-Role'] || headers['x-user-role'] || 'user'
    } 
  });
};

// Add a debug function to test POST requests with headers
api.testPostHeaders = async () => {
  try {
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : { username: 'test-user', role: 'test-role' };
    
    //console.log('Testing POST request headers with user:', user);
    
    // Create headers explicitly
    const headers = {
      'X-User-Name': user.username,
      'X-User-Role': user.role,
      'x-user-name': user.username,
      'x-user-role': user.role
    };
    
    //console.log('Explicit headers being set:', headers);
    
    // Make a POST request to the debug endpoint
    const response = await api.post('/api/clients/debug-headers', { test: 'data' }, { headers });
    //console.log('Debug POST response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error testing POST headers:', error);
    return { error: error.message };
  }
};

// Add a function to test the postWithAuth method
api.testPostWithAuth = async () => {
  try {
    //console.log('Testing postWithAuth method');
    
    // Make a POST request to the debug endpoint using postWithAuth
    const response = await api.postWithAuth('/api/clients/debug-headers', { test: 'data' });
    //console.log('Debug postWithAuth response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error testing postWithAuth:', error);
    return { error: error.message };
  }
};

// Add a function to test POST requests with explicit headers
api.testPostWithExplicitHeaders = async (url = '/api/debug-post') => {
  try {
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : { username: 'test-user', role: 'test-role' };
    
    //console.log('Testing POST request with explicit headers');
    //console.log('User from localStorage:', user);
    
    // Create explicit headers
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Name': user.username,
      'X-User-Role': user.role,
      'x-user-name': user.username,
      'x-user-role': user.role
    };
    
    //console.log('Explicit headers being set:', headers);
    
    // Make a POST request with explicit headers
    const response = await axios.post(
      `${apiConfig.API_BASE_URL}${url}`, 
      { test: 'data', timestamp: new Date().toISOString() }, 
      { headers }
    );
    
    //console.log('POST response with explicit headers:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error testing POST with explicit headers:', error);
    return { error: error.message };
  }
};

// Add a helper function for making POST requests with explicit headers
api.postWithExplicitHeaders = async (url, data, options = {}) => {
  try {
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    let username = 'unknown';
    let role = 'user';
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        username = user.username || 'unknown';
        role = user.role || 'user';
        //console.log('User from localStorage for POST request:', user);
      } catch (e) {
        console.warn('Failed to parse user data for POST request', e);
      }
    }
    
    // Create headers with user info
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Name': username,
      'X-User-Role': role,
      'x-user-name': username,
      'x-user-role': role,
    };
    
    // Merge with any headers provided in options
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        headers[key] = options.headers[key];
      });
    }
    
    //console.log('Making POST request with explicit headers:', headers);
    
    // Use fetch API directly
    const fullUrl = `${apiConfig.API_BASE_URL}${url}`;
    //console.log('POST request URL:', fullUrl);
    //console.log('POST request data:', data);
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      mode: 'cors'
    });
    
    // Check if response is ok
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Request failed with status ${response.status}`);
    }
    
    // Parse response
    const responseData = await response.json();
    return { data: responseData, status: response.status };
  } catch (error) {
    console.error('Error in postWithExplicitHeaders:', error);
    throw error;
  }
};

// Add a test function for POST requests with headers
api.testDirectPostWithHeaders = async () => {
  try {
    //console.log('Testing direct POST request with headers');
    
    // Hardcoded user info for testing
    const username = 'gaurav_landge';
    const role = 'admin';
    
    // Create headers with explicit user info
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Name': username,
      'X-User-Role': role
    };
    
    //console.log('POST request headers:', headers);
    
    // Make the fetch request
    const response = await fetch(
      `${apiConfig.API_BASE_URL}/api/clients/debug-headers`,
      {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ test: 'data', timestamp: new Date().toISOString() }),
        mode: 'cors'
      }
    );
    
    const data = await response.json();
    //console.log('Debug POST response:', data);
    
    return data;
  } catch (error) {
    console.error('Error testing direct POST with headers:', error);
    return { error: error.message };
  }
};

// Add a function to test POST requests with explicit headers for license saving
api.testLicenseSave = async (clientName) => {
  try {
    //console.log('Testing license save with explicit headers');
    
    // Hardcoded user info for testing
    const username = 'test_user';
    const role = 'admin';
    
    // Test data
    const testData = {
      startDate: '2025-07-01',
      endDate: '2025-12-31',
      licensePerDay: '100',
      associatedLogData: 'Test data'
    };
    
    // Create headers with explicit user info
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Name': username,
      'X-User-Role': role
    };
    
    //console.log('POST request headers:', headers);
    
    // Make the fetch request
    const response = await fetch(
      `${apiConfig.API_BASE_URL}/api/clients/${clientName}/license`,
      {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(testData),
        mode: 'cors'
      }
    );
    
    const data = await response.json();
    //console.log('License save test response:', data);
    
    return data;
  } catch (error) {
    console.error('Error testing license save:', error);
    return { error: error.message };
  }
};

// Add a function to test the debug-headers-test endpoint
api.testDebugHeadersEndpoint = async () => {
  try {
    //console.log('Testing debug-headers-test endpoint');
    
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    let username = 'unknown';
    let role = 'user';
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        username = user.username || 'unknown';
        role = user.role || 'user';
        //console.log('Using username and role for debug-headers-test:', username, role);
      } catch (e) {
        console.warn('Failed to parse user data from localStorage', e);
      }
    }
    
    // Create headers with explicit user info
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Name': username,
      'X-User-Role': role
    };
    
    //console.log('Debug-headers-test headers:', headers);
    
    // Make the fetch request
    const response = await fetch(
      `${apiConfig.API_BASE_URL}/api/clients/debug-headers-test`,
      {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ test: 'data', timestamp: new Date().toISOString() }),
        mode: 'cors'
      }
    );
    
    const data = await response.json();
    //console.log('Debug-headers-test response:', data);
    
    return data;
  } catch (error) {
    console.error('Error testing debug-headers-test:', error);
    return { error: error.message };
  }
};

// Add a helper method for making GET requests with explicit headers
api.getWithAuth = async (url, options = {}) => {
  // Get user info from localStorage
  const userStr = localStorage.getItem('user');
  const headers = options.headers || {};
  
  // Log the current state of headers before modification
  //console.log('Initial headers for GET request:', headers);
  
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      //console.log('User from localStorage:', user);
      
      if (user.username) {
        // Set both uppercase and lowercase versions to ensure compatibility
        headers['x-user-name'] = user.username;
        headers['X-User-Name'] = user.username;
        //console.log(`Setting username headers for GET request: ${user.username}`);
      } else {
        headers['x-user-name'] = 'unknown';
        headers['X-User-Name'] = 'unknown';
      }
      
      if (user.role) {
        // Set both uppercase and lowercase versions to ensure compatibility
        headers['x-user-role'] = user.role;
        headers['X-User-Role'] = user.role;
        //console.log(`Setting role headers for GET request: ${user.role}`);
      } else {
        headers['x-user-role'] = 'user';
        headers['X-User-Role'] = 'user';
      }
    } catch (e) {
      console.warn('Failed to parse user data in getWithAuth', e);
      // Set default values
      headers['x-user-name'] = 'unknown';
      headers['X-User-Name'] = 'unknown';
      headers['x-user-role'] = 'user';
      headers['X-User-Role'] = 'user';
    }
  } else {
    console.warn('No user found in localStorage for GET request');
    // No user in localStorage, set default values
    headers['x-user-name'] = 'unknown';
    headers['X-User-Name'] = 'unknown';
    headers['x-user-role'] = 'user';
    headers['X-User-Role'] = 'user';
  }
  
  // Log the final headers before making the request
  //console.log('Final GET request headers:', headers);
  //console.log('GET request URL:', url);
  
  // Make the GET request with the updated headers
  // Ensure we're using the axios instance that has the interceptor
  return api.get(url, { 
    ...options, 
    headers: {
      ...headers,
      'X-User-Name': headers['X-User-Name'] || headers['x-user-name'] || 'unknown',
      'X-User-Role': headers['X-User-Role'] || headers['x-user-role'] || 'user'
    } 
  });
};

// Add a function to test GET requests with explicit headers
api.testGetWithHeaders = async (url = '/api/debug-headers') => {
  try {
    // Get user info from localStorage
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : { username: 'test-user', role: 'test-role' };
    
    //console.log('Testing GET request with explicit headers');
    //console.log('User from localStorage:', user);
    
    // Create explicit headers
    const headers = {
      'Content-Type': 'application/json',
      'X-User-Name': user.username,
      'X-User-Role': user.role,
      'x-user-name': user.username,
      'x-user-role': user.role
    };
    
    //console.log('Explicit headers being set for GET request:', headers);
    
    // Make a GET request with explicit headers
    const response = await axios.get(
      `${apiConfig.API_BASE_URL}${url}`, 
      { headers }
    );
    
    //console.log('GET response with explicit headers:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error testing GET with explicit headers:', error);
    return { error: error.message };
  }
};

export default api;